import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { forkJoin } from 'rxjs';
import { RentalService } from '../../../core/api/rental.service';
import { ExtraServiceService } from '../../../core/api/extra-service.service';
import { Image } from '../../../core/models/image.model';
import { Rental, RentalExtraService, RentalUpdate } from '../../../core/models/rental.model';
import { ExtraService } from '../../../core/models/extra-service.model';
import { parseApiDateTime, toApiDateTime } from '../../../shared/utils/date.utils';
import { DatetimeInputComponent } from '../../../shared/components/datetime-input/datetime-input.component';
import { ImageUploadComponent } from '../../../shared/components/image-upload/image-upload.component';

@Component({
  selector: 'app-rental-edit',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule, ReactiveFormsModule, RouterModule,
    MatCardModule, MatIconModule, MatButtonModule,
    MatFormFieldModule, MatInputModule, MatCheckboxModule,
    MatSnackBarModule, MatProgressSpinnerModule,
    MatDividerModule,
    DatetimeInputComponent, ImageUploadComponent,
  ],
  templateUrl: './rental-edit.component.html',
  styleUrl: './rental-edit.component.scss',
})
export class RentalEditComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly rentalService = inject(RentalService);
  private readonly extraServiceService = inject(ExtraServiceService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly form: FormGroup;
  loading = true;
  submitting = false;
  protected rentalId: number | null = null;
  /** Список всех активных услуг, который рисуем чекбоксами. */
  extraServices: ExtraService[] = [];

  private readonly apiFieldToControl: Record<string, string> = {
    mileage_before: 'mileageBefore',
    mileage_after: 'mileageAfter',
    handover_location: 'handoverLocation',
    handover_datetime: 'handoverDate',
    return_location: 'returnLocation',
    return_datetime: 'returnDate',
    expected_return_datetime: 'expectedReturnDate',
    fuel_level_before: 'fuelLevelBefore',
    fuel_level_after: 'fuelLevelAfter',
    notes: 'notes',
    route: 'route',
    images_before: 'imagesBefore',
    images_after: 'imagesAfter',
    extra_services: 'extraServices',
  };

  constructor() {
    this.form = this.fb.group({
      handoverDate: [null as Date | null, Validators.required],
      returnDate: [null as Date | null],
      expectedReturnDate: [null as Date | null],
      fuelLevelBefore: [null as number | null, Validators.min(0)],
      fuelLevelAfter: [null as number | null, Validators.min(0)],
      mileageBefore: [0, [Validators.required, Validators.min(0)]],
      mileageAfter: [null as number | null, Validators.min(0)],
      handoverLocation: ['', Validators.required],
      returnLocation: [''],
      notes: [''],
      route: [''],
      // CVA-компонент `app-image-upload` хранит `RentalImage[]` для превью;
      // при сабмите маппим в `images_*: number[]` для backend.
      imagesBefore: [[] as Image[]],
      imagesAfter: [[] as Image[]],
      // FormArray под доп. услуги. На каждую услугу — FormGroup
      // (`service` id disabled, `selected` bool, `amount` number|null).
      extraServices: this.fb.array([] as FormGroup[]),
    });
  }

  get extraServicesForm(): FormArray<FormGroup> {
    return this.form.get('extraServices') as FormArray<FormGroup>;
  }

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) {
      this.router.navigate(['/rentals']);
      return;
    }
    this.rentalId = id;
    // Загружаем параллельно прокат и список активных услуг.
    forkJoin({
      rental: this.rentalService.get(id),
      services: this.extraServiceService.list(true),
    }).subscribe({
      next: ({ rental, services }) => {
        this.extraServices = services;
        this.patchForm(rental);
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
        this.snackBar.open('Прокат не найден', 'ОК', { duration: 3000 });
        this.router.navigate(['/rentals']);
      },
    });
  }

  private patchForm(r: Rental): void {
    // CVA-компонент `app-datetime-input` сам парсит ISO/DRF-строки от backend
    // в `Date | null` через `splitDateTimeToParts`.

    // Используем `setValue` (а не `patchValue`): он строже и
    // для Material-инпута надёжнее записывает значение в DOM
    // при первом рендере.
    this.form.setValue({
      handoverDate: r.handover_datetime ?? null,
      returnDate: r.return_datetime ?? null,
      expectedReturnDate: r.expected_return_datetime ?? null,
      fuelLevelBefore: r.fuel_level_before,
      fuelLevelAfter: r.fuel_level_after,
      mileageBefore: r.mileage_before,
      mileageAfter: r.mileage_after,
      handoverLocation: r.handover_location,
      returnLocation: r.return_location,
      notes: r.notes ?? '',
      route: r.route ?? '',
      imagesBefore: r.images_before ?? [],
      imagesAfter: r.images_after ?? [],
      // extraServices заполняется отдельно через FormArray.
      extraServices: [],
    });

    this.buildExtraServicesForm(this.extraServices, r.extra_services ?? []);

    // Переводим форму в `touched`-нетронутое состояние, чтобы
    // ошибки валидации не подсвечивались сразу после загрузки.
    this.form.markAsPristine();
    this.form.markAsUntouched();

    // Патч значений срабатывает синхронно внутри подписки, а
    // рендер input'ов — в следующем change-detection-цикле.
    // С `OnPush` без явного `detectChanges` есть шанс, что
    // браузер отрисует форму с устаревшим `input.value` (пустым).
    // Поэтому форсируем проверку сразу после установки значений.
    this.loading = false;
    this.cdr.detectChanges();
  }

  /**
   * Строит FormArray под список активных услуг, предзаполняя
   * `selected`/`amount` по уже привязанным к прокату услугам
   * (матч по `service` id).
   */
  private buildExtraServicesForm(services: ExtraService[], bound: RentalExtraService[]): void {
    while (this.extraServicesForm.length > 0) {
      this.extraServicesForm.removeAt(0);
    }
    for (const es of services) {
      const linked = bound.find((b) => b.service === es.id);
      const selected = !!linked;
      const amount = linked ? Number(linked.amount) : null;
      const group = this.fb.group({
        service: [{ value: es.id, disabled: true }, Validators.required],
        selected: [selected],
        amount: this.fb.control<number | null>(Number.isFinite(amount) ? amount : null, [Validators.min(0.01)]),
      });
      this.extraServicesForm.push(group);
    }
  }

  /**
   * Сбор `extra_services` из FormArray. В режиме редактирования ВСЕГДА
   * отправляем полный набор (включая пустой `[]`) — у бэка replace-режим,
   * поэтому отсутствие поля оставит старый набор нетронутым.
   */
  private collectExtraServices(): { service: number; amount: number }[] {
    return this.extraServicesForm.controls
      .map((group) => group.getRawValue() as { service: number; selected: boolean; amount: number | null })
      .filter((row) => row.selected && row.amount != null && Number(row.amount) > 0)
      .map((row) => ({ service: row.service, amount: Number(row.amount) }));
  }

  goBack(): void {
    if (this.rentalId) {
      this.router.navigate(['/rentals', this.rentalId]);
    } else {
      this.router.navigate(['/rentals']);
    }
  }

  onSubmit(): void {
    if (this.form.invalid || this.submitting || !this.rentalId) return;
    this.submitting = true;
    const v = this.form.value;
    const handoverDate = v.handoverDate ? parseApiDateTime(v.handoverDate) : null;
    const returnDate = v.returnDate ? parseApiDateTime(v.returnDate) : null;
    const expectedReturnDate = v.expectedReturnDate ? parseApiDateTime(v.expectedReturnDate) : null;
    const data: RentalUpdate = {
      mileage_before: v.mileageBefore,
      mileage_after: v.mileageAfter === '' || v.mileageAfter === null ? null : v.mileageAfter,
      handover_location: v.handoverLocation,
      handover_datetime: handoverDate ? toApiDateTime(handoverDate) : null,
      return_location: v.returnLocation,
      return_datetime: returnDate ? toApiDateTime(returnDate) : null,
      expected_return_datetime: expectedReturnDate ? toApiDateTime(expectedReturnDate) : null,
      fuel_level_before: v.fuelLevelBefore === '' || v.fuelLevelBefore === null ? null : v.fuelLevelBefore,
      fuel_level_after: v.fuelLevelAfter === '' || v.fuelLevelAfter === null ? null : v.fuelLevelAfter,
      notes: v.notes || '',
      route: v.route || '',
      // PATCH с массивом ID полностью заменяет m2m-набор —
      // отправляем полный текущий список (включая удалённые).
      images_before: (v.imagesBefore || []).map((img: Image) => img.id),
      images_after: (v.imagesAfter || []).map((img: Image) => img.id),
      // replace-режим: даже пустой массив отправляем, чтобы снять все услуги.
      extra_services: this.collectExtraServices(),
    };
    this.rentalService.update(this.rentalId, data).subscribe({
      next: () => {
        this.snackBar.open('Прокат сохранён', 'ОК', { duration: 3000 });
        this.router.navigate(['/rentals', this.rentalId]);
      },
      error: (err: any) => {
        this.submitting = false;
        const errorData = err.error;
        if (errorData && typeof errorData === 'object' && !Array.isArray(errorData)) {
          this.setServerErrors(errorData);
          // Вложенные ошибки (`extra_services`) бэк может прислать без
          // маппинга на конкретный контрол — показываем в snackBar.
          const nonField = errorData.all || errorData.non_field_errors || errorData.extra_services;
          if (nonField) {
            const msg = Array.isArray(nonField) ? nonField[0] : String(nonField);
            this.snackBar.open(msg, 'ОК', { duration: 5000 });
          }
        } else {
          const msg = Array.isArray(errorData) ? errorData[0] : 'Ошибка сохранения';
          this.snackBar.open(msg, 'ОК', { duration: 5000 });
        }
      },
    });
  }

  private setServerErrors(errors: Record<string, any>): void {
    for (const [apiField, controlName] of Object.entries(this.apiFieldToControl)) {
      const control = this.form.get(controlName);
      if (control && errors.hasOwnProperty(apiField)) {
        const messages = errors[apiField];
        const msg = Array.isArray(messages) ? messages[0] : String(messages);
        control.setErrors({ ...control.errors, serverError: msg });
      }
    }
  }
}
